/**
 * MapStage Component
 *
 * A Konva Stage component with built-in pan and zoom functionality.
 * Used as the base canvas for both the gym map viewer and editor.
 *
 * Features:
 * - Mouse drag to pan
 * - Mouse wheel to zoom (zooms toward cursor position)
 * - Touch pinch-to-zoom
 * - Touch drag-to-pan
 * - Configurable zoom limits
 * - Optional drag bounds to keep content visible
 *
 * @example
 * ```tsx
 * import { MapStage } from '@/components/gym-map/MapStage';
 * import { Image as KonvaImage } from 'react-konva';
 *
 * const FloorPlanViewer = () => {
 *   const { image, isLoaded } = useFloorPlanImage(imageUrl);
 *
 *   return (
 *     <MapStage width={800} height={600} imageWidth={1920} imageHeight={1080}>
 *       {isLoaded && <KonvaImage image={image} />}
 *     </MapStage>
 *   );
 * };
 * ```
 */

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type ReactNode,
  type ForwardedRef,
} from 'react';
import { Stage, Layer } from 'react-konva';
import type Konva from 'konva';
import { usePreventBodyScroll } from '@/hooks/usePreventBodyScroll';
import { perfStart, perfEnd, perfRender } from '@/utils/performanceMonitor';

/**
 * Map viewport state
 */
export interface ViewportState {
  /** X position offset */
  x: number;
  /** Y position offset */
  y: number;
  /** Zoom scale (1 = 100%) */
  scale: number;
}

/**
 * Imperative handle for MapStage component
 */
export interface MapStageRef {
  /** Animate viewport to target state */
  animateToViewport: (target: ViewportState, duration?: number) => Promise<void>;
  /** Get current viewport state */
  getViewport: () => ViewportState;
  /** Cancel any ongoing animation */
  cancelAnimation: () => void;
}

/**
 * Props for MapStage component
 */
export interface MapStageProps {
  /** Stage width in pixels */
  width: number;
  /** Stage height in pixels */
  height: number;
  /** Image/content width in pixels (for drag bounds calculation) */
  imageWidth?: number;
  /** Image/content height in pixels (for drag bounds calculation) */
  imageHeight?: number;
  /** Minimum zoom scale (default: 0.5) */
  minScale?: number;
  /** Maximum zoom scale (default: 3) */
  maxScale?: number;
  /** Initial viewport state */
  initialViewport?: Partial<ViewportState>;
  /** Called when viewport changes (pan or zoom) */
  onViewportChange?: (viewport: ViewportState) => void;
  /** Whether dragging/panning is enabled (default: true) */
  draggable?: boolean;
  /** Children to render inside the stage */
  children?: ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Callback when stage is clicked (not on child elements) */
  onClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  /** Callback when stage is tapped (touch, not on child elements) */
  onTap?: (event: Konva.KonvaEventObject<TouchEvent>) => void;
  /** Callback when stage is double-clicked */
  onDblClick?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
  /** Callback when mouse moves over the stage */
  onMouseMove?: (event: Konva.KonvaEventObject<MouseEvent>) => void;

  /** Whether to prevent body scroll during map interaction (default: true) */
  preventBodyScroll?: boolean;
}

/** Default minimum zoom scale */
const DEFAULT_MIN_SCALE = 0.5;

/** Default maximum zoom scale */
const DEFAULT_MAX_SCALE = 3;

/** Zoom factor per wheel event */
const ZOOM_FACTOR = 1.1;

/** Default animation duration in milliseconds */
const DEFAULT_ANIMATION_DURATION = 400;

/**
 * Easing function: easeInOutCubic
 * Smooth acceleration and deceleration
 */
const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

/**
 * Distance between two touch points
 */
const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Center point between two touches
 */
const getTouchCenter = (touch1: Touch, touch2: Touch): { x: number; y: number } => ({
  x: (touch1.clientX + touch2.clientX) / 2,
  y: (touch1.clientY + touch2.clientY) / 2,
});

/**
 * MapStage Component
 *
 * Provides a pannable and zoomable canvas for displaying floor plans
 * and wall section polygons.
 */
const MapStageInner = (
  {
    width,
    height,
    imageWidth,
    imageHeight,
    minScale = DEFAULT_MIN_SCALE,
    maxScale = DEFAULT_MAX_SCALE,
    initialViewport,
    onViewportChange,
    draggable = true,
    children,
    className,
    onClick,
    onTap,
    onDblClick,
    onMouseMove,
    preventBodyScroll = true,
  }: MapStageProps,
  ref: ForwardedRef<MapStageRef>
) => {
  // Performance tracking
  perfRender('MapStage');

  // Track if initial viewport has been applied
  const hasInitializedRef = useRef(false);

  // Viewport state
  const [viewport, setViewport] = useState<ViewportState>({
    x: initialViewport?.x ?? 0,
    y: initialViewport?.y ?? 0,
    scale: initialViewport?.scale ?? 1,
  });

  // Only apply initialViewport once on first valid values (not on every change)
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      initialViewport &&
      initialViewport.x !== undefined &&
      initialViewport.y !== undefined &&
      initialViewport.scale !== undefined
    ) {
      setViewport({
        x: initialViewport.x,
        y: initialViewport.y,
        scale: initialViewport.scale,
      });
      hasInitializedRef.current = true;
    }
  }, [initialViewport]);

  // Ref for accessing stage instance
  const stageRef = useRef<Konva.Stage>(null);

  // Ref for container element (for preventing body scroll)
  const containerRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll during map interaction
  usePreventBodyScroll(containerRef, { enabled: preventBodyScroll });

  // Touch state for pinch-to-zoom
  const lastTouchDistance = useRef<number | null>(null);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  const isPinching = useRef(false);
  // Working viewport during gestures (avoids stale closures)
  const workingViewport = useRef<ViewportState>(viewport);

  // Animation state
  const animationFrameRef = useRef<number | null>(null);
  const animationResolveRef = useRef<(() => void) | null>(null);

  // Throttle state for viewport change notifications during gestures
  // Uses requestAnimationFrame for efficient, display-synced updates
  const viewportChangeRafRef = useRef<number | null>(null);
  const lastViewportChangeTime = useRef<number>(0);
  const VIEWPORT_CHANGE_MIN_INTERVAL = 32; // ~30fps for state updates

  /**
   * Cancel any ongoing animation
   */
  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (animationResolveRef.current) {
      animationResolveRef.current();
      animationResolveRef.current = null;
    }
  }, []);

  /**
   * Animate viewport to target state
   */
  const animateToViewport = useCallback(
    (target: ViewportState, duration: number = DEFAULT_ANIMATION_DURATION): Promise<void> => {
      return new Promise((resolve) => {
        // Cancel any existing animation
        cancelAnimation();

        const stage = stageRef.current;
        if (!stage) {
          resolve();
          return;
        }

        // Get current state from stage (most accurate during animations)
        const startX = stage.x();
        const startY = stage.y();
        const startScale = stage.scaleX();

        const startTime = performance.now();

        // Store resolve for potential cancellation
        animationResolveRef.current = resolve;

        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = easeInOutCubic(progress);

          // Interpolate values
          const newX = startX + (target.x - startX) * easedProgress;
          const newY = startY + (target.y - startY) * easedProgress;
          const newScale = startScale + (target.scale - startScale) * easedProgress;

          // Update stage directly for smooth animation
          stage.position({ x: newX, y: newY });
          stage.scale({ x: newScale, y: newScale });
          stage.batchDraw();

          if (progress < 1) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            // Animation complete - sync to React state
            const finalViewport = { x: target.x, y: target.y, scale: target.scale };
            workingViewport.current = finalViewport;
            setViewport(finalViewport);
            onViewportChange?.(finalViewport);
            animationFrameRef.current = null;
            animationResolveRef.current = null;
            resolve();
          }
        };

        animationFrameRef.current = requestAnimationFrame(animate);
      });
    },
    [cancelAnimation, onViewportChange]
  );

  /**
   * Get current viewport state
   */
  const getViewport = useCallback((): ViewportState => {
    const stage = stageRef.current;
    if (stage) {
      return {
        x: stage.x(),
        y: stage.y(),
        scale: stage.scaleX(),
      };
    }
    return viewport;
  }, [viewport]);

  // Expose imperative API
  useImperativeHandle(
    ref,
    () => ({
      animateToViewport,
      getViewport,
      cancelAnimation,
    }),
    [animateToViewport, getViewport, cancelAnimation]
  );

  // Cleanup animation and throttle frames on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (viewportChangeRafRef.current !== null) {
        cancelAnimationFrame(viewportChangeRafRef.current);
      }
    };
  }, []);

  // Keep workingViewport in sync with state when not gesturing
  useEffect(() => {
    if (!isPinching.current) {
      workingViewport.current = viewport;
    }
  }, [viewport]);

  /**
   * Update viewport and notify parent
   */
  const updateViewport = useCallback(
    (newViewport: ViewportState) => {
      workingViewport.current = newViewport;
      setViewport(newViewport);
      onViewportChange?.(newViewport);
    },
    [onViewportChange]
  );

  /**
   * Clamp scale within min/max bounds
   */
  const clampScale = useCallback(
    (scale: number): number => {
      return Math.max(minScale, Math.min(maxScale, scale));
    },
    [minScale, maxScale]
  );

  /**
   * Calculate drag bounds to keep some content visible
   * Note: Konva's dragBoundFunc passes (pos, event), so we ignore the second arg
   * and always get scale from the stage ref or state
   *
   * The bounds are generous to allow:
   * 1. Centering any point on the image (important for edge pins)
   * 2. Keeping at least some image visible to prevent confusion
   */
  const getDragBounds = useCallback(
    (pos: { x: number; y: number }): { x: number; y: number } => {
      if (!imageWidth || !imageHeight) {
        return pos;
      }

      // Always get scale from stage ref or fall back to state
      const effectiveScale = stageRef.current?.scaleX() ?? viewport.scale;
      const scaledWidth = imageWidth * effectiveScale;
      const scaledHeight = imageHeight * effectiveScale;

      // Calculate bounds that allow centering any point on the image
      // while still keeping at least 15% of the viewport showing image content
      const viewportCenterX = width / 2;
      const viewportCenterY = height / 2;
      const minVisibleFraction = 0.15;

      // Allow centering any X point on the image
      // To center image point 0: stage.x = viewportCenterX
      // To center image point imageWidth: stage.x = viewportCenterX - scaledWidth
      // Add some margin to keep at least minVisibleFraction visible
      const xMargin = Math.min(width * minVisibleFraction, scaledWidth * 0.5);
      const minX = viewportCenterX - scaledWidth - xMargin;
      const maxX = viewportCenterX + xMargin;

      // Same logic for Y
      const yMargin = Math.min(height * minVisibleFraction, scaledHeight * 0.5);
      const minY = viewportCenterY - scaledHeight - yMargin;
      const maxY = viewportCenterY + yMargin;

      return {
        x: Math.max(minX, Math.min(maxX, pos.x)),
        y: Math.max(minY, Math.min(maxY, pos.y)),
      };
    },
    [imageWidth, imageHeight, width, height, viewport.scale]
  );

  /**
   * Handle drag start - cancel any animation
   */
  const handleDragStart = useCallback(() => {
    cancelAnimation();
  }, [cancelAnimation]);

  /**
   * Handle drag move - update working ref and throttle state sync
   * Uses requestAnimationFrame for efficient, display-synced updates
   */
  const handleDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      const stage = e.target;
      if (stage !== stageRef.current) return;

      // Update the working ref immediately for smooth rendering
      workingViewport.current = {
        ...workingViewport.current,
        x: stage.x(),
        y: stage.y(),
      };

      // Throttled sync to React state using requestAnimationFrame
      // Only schedule if we don't already have one pending
      if (viewportChangeRafRef.current === null) {
        viewportChangeRafRef.current = requestAnimationFrame(() => {
          const now = performance.now();
          // Only update if enough time has passed (throttle)
          if (now - lastViewportChangeTime.current >= VIEWPORT_CHANGE_MIN_INTERVAL) {
            const currentViewport = workingViewport.current;
            setViewport(currentViewport);
            onViewportChange?.(currentViewport);
            lastViewportChangeTime.current = now;
          }
          viewportChangeRafRef.current = null;
        });
      }
    },
    [onViewportChange]
  );

  /**
   * Handle drag end (pan)
   */
  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      const stage = e.target;
      if (stage !== stageRef.current) return;

      updateViewport({
        ...viewport,
        x: stage.x(),
        y: stage.y(),
      });
    },
    [viewport, updateViewport]
  );

  /**
   * Handle wheel event (zoom)
   */
  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      perfStart('handleWheel');
      e.evt.preventDefault();

      // Cancel any ongoing animation
      cancelAnimation();

      const stage = stageRef.current;
      if (!stage) {
        perfEnd('handleWheel');
        return;
      }

      // Get pointer position relative to stage
      const pointer = stage.getPointerPosition();
      if (!pointer) {
        perfEnd('handleWheel');
        return;
      }

      // Use current stage values to avoid stale state
      const currentScale = stage.scaleX();
      const currentX = stage.x();
      const currentY = stage.y();

      // Determine zoom direction
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const newScale = clampScale(
        direction > 0 ? currentScale * ZOOM_FACTOR : currentScale / ZOOM_FACTOR
      );

      // Don't update if scale hasn't changed (at limits)
      if (newScale === currentScale) {
        perfEnd('handleWheel');
        return;
      }

      // Calculate new position to zoom toward cursor
      const scaleRatio = newScale / currentScale;
      const newX = pointer.x - (pointer.x - currentX) * scaleRatio;
      const newY = pointer.y - (pointer.y - currentY) * scaleRatio;

      // Apply drag bounds if configured
      const boundedPos = getDragBounds({ x: newX, y: newY });

      // Update working viewport ref
      workingViewport.current = {
        x: boundedPos.x,
        y: boundedPos.y,
        scale: newScale,
      };

      // Directly update stage for smooth performance (bypass React re-render)
      stage.position(boundedPos);
      stage.scale({ x: newScale, y: newScale });
      stage.batchDraw();

      // Sync to React state after a short delay (debounced)
      // This prevents re-renders during rapid wheel events
      setTimeout(() => {
        const finalViewport = workingViewport.current;
        setViewport(finalViewport);
        onViewportChange?.(finalViewport);
      }, 100);

      perfEnd('handleWheel');
    },
    [cancelAnimation, clampScale, getDragBounds, onViewportChange]
  );

  /**
   * Handle touch start (for pinch-to-zoom)
   */
  const handleTouchStart = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      // Cancel any ongoing animation on touch
      cancelAnimation();

      const touches = e.evt.touches;
      const touch0 = touches[0];
      const touch1 = touches[1];
      if (touches.length === 2 && touch0 && touch1) {
        // Start of pinch gesture
        isPinching.current = true;
        lastTouchDistance.current = getTouchDistance(touch0, touch1);
        lastTouchCenter.current = getTouchCenter(touch0, touch1);

        // Capture current viewport state and disable Konva's drag during pinch
        const stage = stageRef.current;
        if (stage) {
          workingViewport.current = {
            x: stage.x(),
            y: stage.y(),
            scale: stage.scaleX(),
          };
          stage.draggable(false);
        }
      }
    },
    [cancelAnimation]
  );

  /**
   * Handle touch move (for pinch-to-zoom)
   * Uses refs and direct stage manipulation to avoid stale closures and React re-renders
   */
  const handleTouchMove = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const touches = e.evt.touches;
      const touch0 = touches[0];
      const touch1 = touches[1];
      if (touches.length !== 2 || !touch0 || !touch1) return;
      if (!lastTouchDistance.current || !lastTouchCenter.current) return;
      if (!isPinching.current) return;

      // Prevent default to stop page scroll during pinch
      e.evt.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;

      // Use working viewport (ref) to avoid stale closure issues
      const currentViewport = workingViewport.current;

      // Calculate new scale from pinch distance
      const currentDistance = getTouchDistance(touch0, touch1);
      const scaleFactor = currentDistance / lastTouchDistance.current;
      const newScale = clampScale(currentViewport.scale * scaleFactor);

      // Get current touch center
      const currentCenter = getTouchCenter(touch0, touch1);

      // Calculate stage-relative position
      const stageRect = stage.container().getBoundingClientRect();
      const stageCenter = {
        x: currentCenter.x - stageRect.left,
        y: currentCenter.y - stageRect.top,
      };

      // Calculate new position to zoom toward center of pinch
      const scaleRatio = newScale / currentViewport.scale;
      const newX = stageCenter.x - (stageCenter.x - currentViewport.x) * scaleRatio;
      const newY = stageCenter.y - (stageCenter.y - currentViewport.y) * scaleRatio;

      // Update working viewport ref
      workingViewport.current = {
        x: newX,
        y: newY,
        scale: newScale,
      };

      // Directly update stage for smooth performance (bypasses React re-render)
      stage.position({ x: newX, y: newY });
      stage.scale({ x: newScale, y: newScale });
      stage.batchDraw();

      // Update last touch state for next frame
      lastTouchDistance.current = currentDistance;
      lastTouchCenter.current = currentCenter;

      // Throttled sync to React state using requestAnimationFrame
      if (viewportChangeRafRef.current === null) {
        viewportChangeRafRef.current = requestAnimationFrame(() => {
          const now = performance.now();
          if (now - lastViewportChangeTime.current >= VIEWPORT_CHANGE_MIN_INTERVAL) {
            const currentViewport = workingViewport.current;
            setViewport(currentViewport);
            onViewportChange?.(currentViewport);
            lastViewportChangeTime.current = now;
          }
          viewportChangeRafRef.current = null;
        });
      }
    },
    [clampScale, onViewportChange]
  );

  /**
   * Handle touch end (reset pinch state and sync to React)
   */
  const handleTouchEnd = useCallback(
    (_e: Konva.KonvaEventObject<TouchEvent>) => {
      // Only finalize if we were pinching
      if (isPinching.current) {
        const stage = stageRef.current;
        if (stage) {
          // Apply drag bounds to final position (getDragBounds reads scale from stage ref)
          const boundedPos = getDragBounds({
            x: workingViewport.current.x,
            y: workingViewport.current.y,
          });

          // Sync final viewport to React state
          const finalViewport = {
            x: boundedPos.x,
            y: boundedPos.y,
            scale: workingViewport.current.scale,
          };

          // Update stage with bounded position
          stage.position(boundedPos);
          stage.batchDraw();

          // Sync to React state and notify parent
          setViewport(finalViewport);
          workingViewport.current = finalViewport;
          onViewportChange?.(finalViewport);

          // Re-enable Konva's drag if it was enabled
          if (draggable) {
            stage.draggable(true);
          }
        }
      }

      // Reset pinch state
      isPinching.current = false;
      lastTouchDistance.current = null;
      lastTouchCenter.current = null;
    },
    [getDragBounds, draggable, onViewportChange]
  );

  /**
   * Handle click event - wrap to avoid passing undefined
   */
  const handleClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      onClick?.(e);
    },
    [onClick]
  );

  /**
   * Handle tap event - wrap to avoid passing undefined
   */
  const handleTap = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      onTap?.(e);
    },
    [onTap]
  );

  /**
   * Handle double-click event - wrap to avoid passing undefined
   */
  const handleDblClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      onDblClick?.(e);
    },
    [onDblClick]
  );

  /**
   * Handle mouse move event - wrap to avoid passing undefined
   */
  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      onMouseMove?.(e);
    },
    [onMouseMove]
  );

  return (
    <div ref={containerRef} className={className} style={{ touchAction: 'none' }}>
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        x={viewport.x}
        y={viewport.y}
        scaleX={viewport.scale}
        scaleY={viewport.scale}
        draggable={draggable}
        dragBoundFunc={getDragBounds}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
        onTap={handleTap}
        onDblClick={handleDblClick}
        onMouseMove={handleMouseMove}
      >
        <Layer>{children}</Layer>
      </Stage>
    </div>
  );
};

/**
 * MapStage component with forwardRef for imperative API
 */
export const MapStage = forwardRef<MapStageRef, MapStageProps>(MapStageInner);

export default MapStage;
